import { useState } from 'react'
import { router } from '@inertiajs/react'
import { MoreHorizontal } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/Button'
import { Textarea } from '@/components/ui/Textarea'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu'
import TimelineItem from './TimelineItem'
import { message_submission_path } from '@/lib/routes'
import { TimeAgo } from '@/components/TimeAgo'
import { copyText } from './copyText'

export interface SubmissionProps {
  id: string
  message: string | null
  submitted_by: string
  submitted_by_avatar_url?: string | null
  submitted_at: string
  // Set only on a finished review.
  resolved_by?: string | null
  resolved_at?: string | null
  can_review: boolean
  can_edit: boolean
}

// The submission message as a GitHub timeline comment: header strip (author
// bold, verb plain, date muted) over the body, and a "..." menu — Copy link,
// plus inline Edit for the author or reviewer.
export default function SubmissionCard({ submission }: { submission: SubmissionProps }) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState('')
  const draftReady = draft.trim().length > 0

  const startEdit = () => {
    setDraft(submission.message ?? '')
    setEditing(true)
  }

  const update = () => {
    router.patch(message_submission_path(submission.id), { message: draft }, { preserveScroll: true })
    setEditing(false)
  }

  return (
    <TimelineItem name={submission.submitted_by} avatarUrl={submission.submitted_by_avatar_url}>
      <div
        className={cn(
          'flex items-center gap-3 px-4 py-2.5 text-sm text-foreground',
          (submission.message || editing) && 'border-b border-border'
        )}
      >
        <div className="min-w-0">
          <span className="font-semibold">{submission.submitted_by}</span>
          <span> submitted for review</span>
          <span className="text-muted-foreground"> · <TimeAgo at={submission.submitted_at} /></span>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              aria-label="Comment actions"
              className="ml-auto h-7 w-7 shrink-0 text-muted-foreground hover:text-foreground"
            >
              <MoreHorizontal size={16} />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => copyText(window.location.href)}>
              Copy link
            </DropdownMenuItem>
            {submission.can_edit && (
              <DropdownMenuItem onClick={startEdit}>Edit</DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {editing ? (
        <div className="flex flex-col gap-2 px-4 py-3">
          <Textarea
            rows={4}
            className="min-h-20 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-chart-muted"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Leave a comment"
            autoFocus
          />
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => setEditing(false)}>Cancel</Button>
            <Button size="sm" disabled={!draftReady} onClick={update}>Update comment</Button>
          </div>
        </div>
      ) : (
        submission.message && (
          <p className="whitespace-pre-line px-4 py-3 text-[13px] leading-relaxed text-foreground">
            {submission.message}
          </p>
        )
      )}
    </TimelineItem>
  )
}
